import { getBrowserSessionDiagnostics } from "@/lib/livekit-agent-telemetry";
import { isExpectedDisconnect, shouldScheduleFreshReconnect } from "@/lib/livekit-reconnect-policy";
import { hasActiveLiveKitConnection } from "@/lib/livekit-session-guards";
import {
  AGENT_DISCONNECTED_CONNECTION_MESSAGE,
  GREETING_PLAYOUT_CONNECTION_MESSAGE,
} from "@/lib/livekit-user-facing-messages";
import { LIVEKIT_PUBLISH_ON_BEHALF_ATTRIBUTE } from "@/lib/livekitTranscriptionStreams";
import {
  ConnectionQuality,
  ConnectionState,
  DisconnectReason,
  type Participant,
  type Room,
  RoomEvent,
} from "livekit-client";
import { useEffect } from "react";

const SESSION_ERROR_ATTRIBUTE = "chicky.session.error";
const SESSION_RECONNECT_REQUIRED_ATTRIBUTE = "chicky.session.reconnect_required";
const AGENT_DISCONNECT_FAILURE_GRACE_MS = 500;

function isConversationalAgent(participant: Participant): boolean {
  return (
    participant.isAgent &&
    !Object.prototype.hasOwnProperty.call(
      participant.attributes ?? {},
      LIVEKIT_PUBLISH_ON_BEHALF_ATTRIBUTE
    )
  );
}

type ConnectionStateMonitorLogger = {
  info: (message: string, data?: unknown) => void;
  warn: (message: string, data?: unknown) => void;
  error: (message: string, data?: unknown) => void;
};

type MutableRef<T> = {
  current: T;
};

type PostHogLike = {
  capture: (eventName: string, properties?: Record<string, unknown>) => void;
};

type ConnectionStateMonitorUser = unknown;

type UseLiveKitConnectionStateMonitorInput<TSessionMode> = {
  currentRoom: Room;
  currentRoomRef: MutableRef<Room>;
  isConnectedRef: MutableRef<boolean>;
  isRecordingRef: MutableRef<boolean>;
  isConnectingRef: MutableRef<boolean>;
  freshReconnectInProgressRef: MutableRef<boolean>;
  freshReconnectTimerRef: MutableRef<NodeJS.Timeout | null>;
  websocket1006RetryCountRef: MutableRef<number>;
  connectionFailureStickyRef: MutableRef<boolean>;
  connectionFailurePhaseRef: MutableRef<string>;
  activeConnectAttemptIdRef: MutableRef<number>;
  tokenCacheRef: MutableRef<unknown>;
  lastRequestedModeRef: MutableRef<TSessionMode>;
  maxWebsocket1006Retries: number;
  isRecording: boolean;
  selectedLanguageCode: string;
  user?: ConnectionStateMonitorUser | null;
  posthogClient?: PostHogLike | null;
  connectToLiveKit: (mode: TSessionMode) => Promise<boolean>;
  markLatencyPhase: (phase: string, data?: Record<string, unknown>) => void;
  setConnectionState: (
    state:
      | "disconnected"
      | "disconnecting"
      | "connecting"
      | "connected"
      | "reconnecting"
      | "failed"
      | "ready"
  ) => void;
  setConnectionError: (error: string | null) => void;
  setConnectionQuality: (quality: ConnectionQuality) => void;
  setIsConnected: (connected: boolean) => void;
  setIsRecording: (recording: boolean) => void;
  logger: ConnectionStateMonitorLogger;
};

export function useLiveKitConnectionStateMonitor<TSessionMode>({
  currentRoom,
  currentRoomRef,
  isConnectedRef,
  isRecordingRef,
  isConnectingRef,
  freshReconnectInProgressRef,
  freshReconnectTimerRef,
  websocket1006RetryCountRef,
  connectionFailureStickyRef,
  connectionFailurePhaseRef,
  activeConnectAttemptIdRef,
  tokenCacheRef,
  lastRequestedModeRef,
  maxWebsocket1006Retries,
  isRecording,
  selectedLanguageCode,
  user,
  posthogClient,
  connectToLiveKit,
  markLatencyPhase,
  setConnectionState,
  setConnectionError,
  setConnectionQuality,
  setIsConnected,
  setIsRecording,
  logger,
}: UseLiveKitConnectionStateMonitorInput<TSessionMode>) {
  useEffect(() => {
    const pendingAgentDisconnectTimers = new Map<string, ReturnType<typeof setTimeout>>();

    const requiresFreshSession = (
      participant: Participant,
      changedAttributes?: Record<string, string>
    ) =>
      isConversationalAgent(participant) &&
      (changedAttributes?.[SESSION_RECONNECT_REQUIRED_ATTRIBUTE] === "true" ||
        participant.attributes?.[SESSION_RECONNECT_REQUIRED_ATTRIBUTE] === "true");

    const failAgentSession = (participant: Participant, source: string) => {
      if (currentRoomRef.current !== currentRoom || connectionFailureStickyRef.current) {
        return;
      }

      const reason =
        participant.attributes?.[SESSION_ERROR_ATTRIBUTE] || "agent_participant_disconnected";
      const isGreetingPlayoutFailure = reason === "initial_greeting_audio_playout_failed";
      connectionFailureStickyRef.current = true;
      connectionFailurePhaseRef.current = isGreetingPlayoutFailure
        ? "initial_greeting_playout"
        : "managed_session";
      tokenCacheRef.current = null;
      isConnectedRef.current = false;
      isRecordingRef.current = false;
      freshReconnectInProgressRef.current = false;
      if (freshReconnectTimerRef.current) {
        clearTimeout(freshReconnectTimerRef.current);
        freshReconnectTimerRef.current = null;
      }

      logger.error("LiveKit agent session requires a fresh connection", {
        source,
        reason,
        participantIdentity: participant.identity,
        roomName: currentRoom.name || null,
      });
      markLatencyPhase("agent_session_reconnect_required", { source, reason });
      setConnectionState("failed");
      setConnectionError(
        isGreetingPlayoutFailure
          ? GREETING_PLAYOUT_CONNECTION_MESSAGE
          : AGENT_DISCONNECTED_CONNECTION_MESSAGE
      );
      setIsConnected(false);
      setIsRecording(false);

      void currentRoom.disconnect().catch((error) => {
        logger.warn("Failed to disconnect unusable LiveKit room", {
          source,
          roomName: currentRoom.name || null,
          error: error instanceof Error ? error.message : String(error),
        });
      });
    };

    const handleParticipantAttributesChanged = (
      changedAttributes: Record<string, string>,
      participant: Participant
    ) => {
      if (requiresFreshSession(participant, changedAttributes)) {
        failAgentSession(participant, "participant_attributes_changed");
      }
    };

    const handleParticipantDisconnected = (participant: Participant) => {
      if (!isConversationalAgent(participant) || currentRoomRef.current !== currentRoom) return;

      if (requiresFreshSession(participant)) {
        failAgentSession(participant, "reconnect_required_agent_disconnected");
        return;
      }

      if (!isRecordingRef.current || connectionFailureStickyRef.current) return;

      const existingTimer = pendingAgentDisconnectTimers.get(participant.identity);
      if (existingTimer) clearTimeout(existingTimer);
      pendingAgentDisconnectTimers.set(
        participant.identity,
        setTimeout(() => {
          pendingAgentDisconnectTimers.delete(participant.identity);
          if (
            currentRoomRef.current !== currentRoom ||
            !isRecordingRef.current ||
            connectionFailureStickyRef.current ||
            currentRoom.state !== ConnectionState.Connected ||
            currentRoom.remoteParticipants.has(participant.identity)
          ) {
            return;
          }
          failAgentSession(participant, "agent_participant_disconnected");
        }, AGENT_DISCONNECT_FAILURE_GRACE_MS)
      );
    };

    const handleConnectionStateChanged = (state: ConnectionState) => {
      if (currentRoomRef.current !== currentRoom) {
        logger.info("Ignoring connection state from an isolated LiveKit room", {
          isolatedRoomName: currentRoom.name || null,
          state,
        });
        return;
      }

      logger.info("LiveKit connection state changed:", state);
      markLatencyPhase(`room_state_${String(state).toLowerCase()}`);

      switch (state) {
        case ConnectionState.Connecting:
          setConnectionState("connecting");
          break;
        case ConnectionState.Connected:
          connectionFailureStickyRef.current = false;
          setConnectionState("connected");
          setConnectionError(null);
          websocket1006RetryCountRef.current = 0;
          break;
        case ConnectionState.Disconnected:
          if (connectionFailureStickyRef.current) {
            logger.info("Preserving failed LiveKit UI state across disconnected room event", {
              roomName: currentRoomRef.current.name || null,
              activeAttemptId: activeConnectAttemptIdRef.current,
              failurePhase: connectionFailurePhaseRef.current,
            });
            break;
          }
          setConnectionState("disconnected");
          break;
        case ConnectionState.Reconnecting:
          setConnectionState("reconnecting");
          break;
      }
    };

    const handleConnectionQualityChanged = (quality: ConnectionQuality) => {
      setConnectionQuality(quality);
    };

    const handleDisconnected = (reason?: DisconnectReason) => {
      if (currentRoomRef.current !== currentRoom) {
        logger.info("Ignoring disconnect event from an isolated LiveKit room", {
          isolatedRoomName: currentRoom.name || null,
          reason: reason ?? "unknown",
        });
        return;
      }

      const reasonName =
        typeof reason === "number"
          ? (DisconnectReason as Record<number, string>)[reason] || String(reason)
          : "unknown";
      logger.warn("LiveKit room disconnected", {
        reason: reason ?? "unknown",
        reasonName,
        wasRecording: isRecording,
        roomName: currentRoom.name || null,
        connectionState: currentRoom.state,
        retryCount: websocket1006RetryCountRef.current,
        isConnected: isConnectedRef.current,
        latestIsRecording: isRecordingRef.current,
        ...getBrowserSessionDiagnostics(),
      });

      if (posthogClient && process.env.NEXT_PUBLIC_POSTHOG_KEY) {
        posthogClient.capture("livekit_disconnected", {
          reason: reason ?? "unknown",
          reason_name: reasonName,
          was_recording: isRecording,
          room_name: currentRoom.name || null,
          room_state: currentRoom.state,
          language_code: selectedLanguageCode,
          retry_count: websocket1006RetryCountRef.current,
          user_type: user ? "authenticated" : "guest",
          browser_online: typeof window !== "undefined" ? window.navigator.onLine : null,
          page_visibility: typeof document !== "undefined" ? document.visibilityState : null,
          page_path: typeof window !== "undefined" ? window.location.pathname : null,
          service_worker_controlled:
            typeof window !== "undefined"
              ? Boolean(window.navigator.serviceWorker?.controller)
              : null,
          node_env: process.env.NODE_ENV,
        });
      }

      const shouldReconnect = shouldScheduleFreshReconnect({
        isRecording,
        reason,
        retryCount: websocket1006RetryCountRef.current,
        maxRetries: maxWebsocket1006Retries,
        roomState: currentRoom.state,
        isConnecting: isConnectingRef.current,
        isFreshReconnectInProgress: freshReconnectInProgressRef.current,
      });

      if (shouldReconnect) {
        logger.warn(
          "Unexpected disconnection detected during active session (possible WebSocket 1006)",
          {
            reason,
            roomName: currentRoom.name || null,
            roomState: currentRoom.state,
            attemptingReconnect: websocket1006RetryCountRef.current < maxWebsocket1006Retries,
            freshReconnectInProgress: freshReconnectInProgressRef.current,
          }
        );

        const retryCount = websocket1006RetryCountRef.current;
        websocket1006RetryCountRef.current = retryCount + 1;
        freshReconnectInProgressRef.current = true;

        const backoffDelay = Math.min(1000 * Math.pow(2, retryCount), 8000);

        logger.info(
          `Scheduling fresh reconnect attempt ${retryCount + 1}/${maxWebsocket1006Retries} in ${backoffDelay}ms`,
          {
            previousRoomName: currentRoom.name || null,
          }
        );
        connectionFailureStickyRef.current = false;
        setConnectionState("reconnecting");
        setConnectionError(
          `Connection lost. Reconnecting... (attempt ${retryCount + 1}/${maxWebsocket1006Retries})`
        );

        freshReconnectTimerRef.current = setTimeout(() => {
          freshReconnectTimerRef.current = null;

          if (!isRecording) {
            logger.info("Skipping fresh reconnect - recording was stopped by user");
            freshReconnectInProgressRef.current = false;
            return;
          }

          if (hasActiveLiveKitConnection(currentRoomRef.current.state)) {
            logger.info("Skipping fresh reconnect - LiveKit room is active again", {
              roomState: currentRoomRef.current.state,
              roomName: currentRoomRef.current.name || null,
            });
            freshReconnectInProgressRef.current = false;
            return;
          }

          tokenCacheRef.current = null;
          logger.info("Executing fresh reconnect after final disconnect", {
            previousRoomName: currentRoom.name || null,
          });
          void connectToLiveKit(lastRequestedModeRef.current).finally(() => {
            freshReconnectInProgressRef.current = false;
          });
        }, backoffDelay);
      } else if (
        isRecording &&
        !isExpectedDisconnect(reason) &&
        websocket1006RetryCountRef.current >= maxWebsocket1006Retries
      ) {
        logger.error("Max WebSocket reconnection attempts reached", {
          maxRetries: maxWebsocket1006Retries,
        });
        connectionFailureStickyRef.current = true;
        setConnectionState("failed");
        setConnectionError("Connection lost. Please try reconnecting manually.");
        setIsRecording(false);

        if (posthogClient && process.env.NEXT_PUBLIC_POSTHOG_KEY) {
          posthogClient.capture("livekit_reconnect_failed", {
            reason: reason ?? "unknown",
            max_retries: maxWebsocket1006Retries,
            language_code: selectedLanguageCode,
            user_type: user ? "authenticated" : "guest",
          });
        }
      }
    };

    currentRoom.on(RoomEvent.ConnectionStateChanged, handleConnectionStateChanged);
    currentRoom.on(RoomEvent.ConnectionQualityChanged, handleConnectionQualityChanged);
    currentRoom.on(RoomEvent.Disconnected, handleDisconnected);
    currentRoom.on(RoomEvent.ParticipantAttributesChanged, handleParticipantAttributesChanged);
    currentRoom.on(RoomEvent.ParticipantDisconnected, handleParticipantDisconnected);

    for (const participant of currentRoom.remoteParticipants.values()) {
      if (requiresFreshSession(participant)) {
        failAgentSession(participant, "existing_participant_attributes");
        break;
      }
    }

    return () => {
      currentRoom.off(RoomEvent.ConnectionStateChanged, handleConnectionStateChanged);
      currentRoom.off(RoomEvent.ConnectionQualityChanged, handleConnectionQualityChanged);
      currentRoom.off(RoomEvent.Disconnected, handleDisconnected);
      currentRoom.off(RoomEvent.ParticipantAttributesChanged, handleParticipantAttributesChanged);
      currentRoom.off(RoomEvent.ParticipantDisconnected, handleParticipantDisconnected);
      for (const timer of pendingAgentDisconnectTimers.values()) clearTimeout(timer);
      pendingAgentDisconnectTimers.clear();
    };
  }, [
    activeConnectAttemptIdRef,
    connectToLiveKit,
    connectionFailurePhaseRef,
    connectionFailureStickyRef,
    currentRoom,
    currentRoomRef,
    freshReconnectInProgressRef,
    freshReconnectTimerRef,
    isConnectedRef,
    isConnectingRef,
    isRecording,
    isRecordingRef,
    lastRequestedModeRef,
    logger,
    markLatencyPhase,
    maxWebsocket1006Retries,
    posthogClient,
    selectedLanguageCode,
    setConnectionError,
    setConnectionQuality,
    setConnectionState,
    setIsConnected,
    setIsRecording,
    tokenCacheRef,
    user,
    websocket1006RetryCountRef,
  ]);
}
